import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type Ref,
} from "react"
import { cva, VariantProps } from "class-variance-authority"
import { cn } from "cn"
import debounce from "lodash/debounce"

import { useRouter } from "@/lib/router"
import IconCheck from "@/components/icons/check"
import IconChevronDown from "@/components/icons/chevron-down"
import IconChevronLeft from "@/components/icons/chevron-left"
import IconChevronRight from "@/components/icons/chevron-right"
import IconChevronUp from "@/components/icons/chevron-up"
import IconDownload from "@/components/icons/download"
import IconGoTo from "@/components/icons/go-to"
import IconMagic from "@/components/icons/magic"
import IconPlayFill from "@/components/icons/play-fill"
import IconPlus from "@/components/icons/plus"
import IconUpload from "@/components/icons/upload"
import {
  resolveIconSpec,
  type IconComponent,
  type IconSpec,
} from "../icons/icon"
import { ButtonOrLink, ButtonOrLinkProps } from "./button-or-link"
import { Loading } from "./loading"

const buttonVariants = cva(
  "group group/button relative z-0 inline-flex items-center justify-center gap-[.3em] rounded-full border border-transparent py-[.25em] text-center font-medium text-balance transition-[transform,border-color,color] focus:opacity-100! disabled:pointer-events-none disabled:opacity-30 data-popup-open:opacity-100!",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-primary-foreground hover:bg-primary-hover active:scale-[.98] data-popup-open:bg-primary-hover",
        knockout: "bg-white text-black hover:bg-white-a-12 active:scale-[.98]",
        outline:
          "border-input bg-background text-foreground group-hover/button:border-current hover:border-current active:scale-[.98] data-popup-open:border-current",
        "outline-clear":
          "border-current bg-transparent text-inherit active:scale-[.98]",
        link: "bg-transparent underline transition-opacity hover:opacity-60",
        subtle:
          "bg-foreground/5 text-foreground hover:bg-foreground/10 active:scale-[.98] data-popup-open:bg-foreground/10",
        "dark-overlay":
          "bg-black-a-7 text-white hover:bg-black-a-9 active:scale-[.98] data-popup-open:bg-black-a-9",
        "subtle-invert":
          "bg-white-a-2 text-white hover:bg-white-a-3 active:scale-[.98] data-popup-open:bg-white-a-3",
        /* removing use of backdrop-blur-2xs causing severe chrome rendering issues */
        overlay:
          "bg-background/92 text-foreground shadow-(--button-shadow) hover:bg-background active:scale-[.98] data-popup-open:bg-background",
        glass:
          "bg-background/75 text-foreground shadow-[0_1px_6px_oklch(0_0_0/0.1)] backdrop-blur-lg backdrop-saturate-150 hover:bg-background/90 active:scale-[.98] data-popup-open:bg-background/90 dark:shadow-[inset_0_0_0_1px_oklch(1_0_0/0.08)]",
        "overlay-light":
          "bg-white/30 text-white backdrop-blur-2xs hover:bg-white/40 active:scale-[.98] data-popup-open:bg-white/40",
        ghost:
          "bg-transparent group-hover/button:bg-foreground/5 hover:bg-foreground/5 data-popup-open:bg-foreground/5",
        destructive:
          "bg-tomato-9 text-tomato-1 hover:bg-tomato-11 active:scale-[.98]",
        warning:
          "bg-orange-3 text-orange-11 hover:border-orange-7 active:scale-[.98]",
        none: "",
      },
      size: {
        xs: "min-h-(--button-xs-size) px-(--button-xs-px) text-2xs leading-[1.125]",
        sm: "min-h-(--button-sm-size) px-(--button-sm-px) text-xs leading-[1.125]",
        md: "min-h-(--button-md-size) px-(--button-md-px) text-sm leading-[1.125]",
        lg: "min-h-(--button-lg-size) px-(--button-lg-px) text-base leading-[1.125]",
        xl: "min-h-(--button-xl-size) px-(--button-xl-px) text-md leading-[1.125]",
        "2xl": "min-h-(--button-2xl-size) px-10 text-lg leading-tight",
      },
      block: {
        true: "flex w-full",
      },
      circle: {
        true: "shrink-0 px-0",
      },
      wide: {
        true: "min-w-[12em]",
      },
      loading: {
        true: "pointer-events-none",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
    compoundVariants: [
      {
        size: "xs",
        circle: true,
        className: "size-(--button-xs-size)",
      },
      {
        size: "sm",
        circle: true,
        className: "size-(--button-sm-size)",
      },
      {
        size: "md",
        circle: true,
        className: "size-(--button-md-size)",
      },
      {
        size: "lg",
        circle: true,
        className: "size-(--button-lg-size)",
      },
      {
        size: "xl",
        circle: true,
        className: "size-(--button-xl-size)",
      },
    ],
  }
)

// Optical adjustments for icons that look too small, large or light beside button text
const iconsWithExtraClasses = new Map<IconComponent, string>([
  [IconGoTo, cn("stroke-[.125em] text-[1.125em]")],
  [IconChevronUp, cn("mt-[.125em] stroke-[2.5] text-[.75em]")],
  [IconChevronDown, cn("mt-[.125em] stroke-[2.5] text-[.75em]")],
  [IconChevronLeft, cn("stroke-2 text-[.875em]")],
  [IconChevronRight, cn("stroke-2 text-[.875em]")],
  [IconPlus, cn("stroke-3 text-[1em]")],
  [IconCheck, cn("stroke-[2.5] text-[1.25em]")],
  [IconMagic, cn("text-[1.25em]")],
  [IconDownload, cn("stroke-[1.75] text-[1.25em]")],
  [IconUpload, cn("stroke-[1.75] text-[1.25em]")],
  [IconPlayFill, cn("text-[1.125em]")],
])

function ButtonIcon({
  spec,
  className,
}: {
  spec: IconSpec
  className: string
}) {
  const { Icon, props } = resolveIconSpec(spec)
  return (
    <Icon
      {...props}
      className={cn(
        className,
        iconsWithExtraClasses.get(Icon),
        props.className
      )}
    />
  )
}

export interface ButtonProps
  extends ButtonOrLinkProps, VariantProps<typeof buttonVariants> {
  iconBefore?: IconSpec | null
  iconAfter?: IconSpec | null
  loading?: boolean
  onPerform?: (e: MouseEvent<HTMLElement>) => Promise<unknown>
}

function Button({
  ref,
  className,
  variant = "primary",
  size = "md",
  block,
  wide,
  circle,
  iconBefore,
  iconAfter,
  loading,
  children,
  href,
  target,
  ...props
}: ButtonProps & { ref?: Ref<HTMLButtonElement | HTMLAnchorElement> }) {
  const [performing, setPerforming] = useState(false)
  const router = useRouter()

  const debouncedSetPerforming = useMemo(
    () => debounce(() => setPerforming(true), 150),
    []
  )

  // Create a wrapper onClick to pass to our internal button that will
  // call either props.onPerform or props.onClick, with the right
  // surrounding behavior.
  const { onPerform, onClick, ...thinnedProps } = props

  const onClickWrapper = useCallback<Required<ButtonOrLinkProps>["onClick"]>(
    async (e) => {
      if (
        href?.startsWith("/") &&
        variant !== "ghost" &&
        variant !== "link" &&
        variant !== "none" &&
        !target
      ) {
        setPerforming(true)
      } else if (onPerform) {
        debouncedSetPerforming()

        try {
          await onPerform(e)
        } finally {
          debouncedSetPerforming.cancel()
          setPerforming(false)
        }
      }
      onClick?.(e)
    },
    [onClick, href, onPerform, variant, target, debouncedSetPerforming]
  )

  useEffect(() => {
    return () => {
      debouncedSetPerforming.cancel()
    }
  }, [debouncedSetPerforming])

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      setPerforming(false)
    }

    router.events.on("routeChangeComplete", handleRouteChangeComplete)
    router.events.on("routeChangeError", handleRouteChangeComplete)

    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete)
      router.events.off("routeChangeError", handleRouteChangeComplete)
    }
  }, [router])

  const isLoading = loading || performing

  return (
    <ButtonOrLink
      ref={ref}
      data-variant={variant}
      data-loading={isLoading}
      className={cn(
        buttonVariants({
          variant,
          size,
          block,
          wide,
          circle,
          loading: isLoading,
        }),
        className
      )}
      {...thinnedProps}
      target={target}
      href={href}
      onClick={onClickWrapper}
    >
      {/* increase tappable area */}
      {circle && (size === "xs" || size === "sm") && (
        <span
          aria-hidden
          className="absolute top-1/2 left-1/2 size-9 -translate-1/2"
        />
      )}
      <span className={cn("contents", isLoading && "invisible")}>
        {iconBefore && (
          <ButtonIcon spec={iconBefore} className="ml-[-.2em] text-[1.5em]" />
        )}
        {children}
        {iconAfter && (
          <ButtonIcon spec={iconAfter} className="mr-[-.2em] text-[1.5em]" />
        )}
      </span>
      {isLoading && (
        <span
          className="absolute top-1/2 left-1/2 -translate-1/2"
          data-testid="button-loader"
        >
          <Loading size={circle && size === "xs" ? ".25em" : ".325em"} />
        </span>
      )}
    </ButtonOrLink>
  )
}
Button.displayName = "Button"

export { Button, buttonVariants }
